import React, { useEffect, useState } from "react";
import Header from "../master/SideBar";
import Nav from "../master/NavBar";
import { Link, useNavigate } from "react-router-dom";
import {
  addStaff,
  VerifyStaffId,
  workingSchedulesList,
  EmploymentAdd,
  getAvailableStaffMenus,
} from "../../api/Staff";
import { formatTime, LoadingDiv, SweetAlertShow } from "../master/Master";
import { subjectList } from "../../api/Subjects";
import { DistrictFetch, StateFetch } from "../../api/LocationService";
import { RoleView } from "../../api/UserRoleServices";
import StaffLayout from "./StaffLayout";
import { appRoleList } from "../../api/Role";

export default function StaffAdd() {
  const [setting, setSetting] = useState({});
  const navigate = useNavigate();
  const [appRoles, setAppRoles] = useState([]);

  useEffect(() => {
    setSetting(JSON.parse(localStorage.getItem("setting") ?? "{}"));
  }, []);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ---------------------------------------------------------------- */
  /*  STEPPER STATE                                                    */
  /* ---------------------------------------------------------------- */
  // activeStep: 1 = Staff Details, 2 = Employment Details
  const [activeStep, setActiveStep] = useState(1);
  const [createdStaffId, setCreatedStaffId] = useState(null);
  const [createdStaffName, setCreatedStaffName] = useState("");
  const [createdRegNo, setCreatedRegNo] = useState("");

  // Whether this install even wants an Employment Details step.
  // If not, we just save + navigate like before (single step).
  const wantsEmploymentStep = true;
  // Array.isArray(setting?.staff_details) &&
  // setting?.staff_details.includes("salary_details");

  /* ---------------------------------------------------------------- */
  /*  STEP 1 - STAFF DETAILS (mostly unchanged from original)          */
  /* ---------------------------------------------------------------- */
  const [subjects, setSubjects] = useState([]);
  const [regDetail, setRegDetail] = useState("");
  const [staff_id, setStaffId] = useState(false); // registration no field
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedAppRoles, setSelectedAppRoles] = useState([]);

  const [requiredMenuRoutes, setRequiredMenuRoutes] = useState([]);
  const [availableMenuGroups, setAvailableMenuGroups] = useState([]);
  const [selectedMenuRoutes, setSelectedMenuRoutes] = useState([]);

  const [staffType, setStaffType] = useState("");

  const handleSubjectChange = (value) => {
    value = Number(value);
    setSelectedSubjects((prev) =>
      prev.includes(value)
        ? prev.filter((id) => id !== value)
        : [...prev, value],
    );
  };

  const handleAppRoleChange = (value) => {
    value = Number(value);
    setSelectedAppRoles((prev) =>
      prev.includes(value)
        ? prev.filter((id) => id !== value)
        : [...prev, value],
    );
  };

  const handleMenuChange = (route) => {
    if (requiredMenuRoutes.includes(route)) {
      return;
    }

    setSelectedMenuRoutes((prev) =>
      prev.includes(route)
        ? prev.filter((item) => item !== route)
        : [...prev, route],
    );
  };

  const handleSelectAllGroupMenus = (group) => {
    const groupRoutes = (group.items || [])
      .map((item) => item.route)
      .filter(Boolean);

    const optionalRoutes = groupRoutes.filter(
      (route) => !requiredMenuRoutes.includes(route),
    );

    const allOptionalSelected =
      optionalRoutes.length > 0 &&
      optionalRoutes.every((route) => selectedMenuRoutes.includes(route));

    setSelectedMenuRoutes((prev) => {
      if (allOptionalSelected) {
        // Deselect optional menus but retain mandatory menus
        return prev.filter(
          (route) =>
            !optionalRoutes.includes(route) ||
            requiredMenuRoutes.includes(route),
        );
      }

      // Select all menus in this group
      return [...new Set([...prev, ...groupRoutes])];
    });
  };

  const getAppRoles = async () => {
    setLoading(true);

    try {
      const response = await appRoleList("");

      if (response?.data?.status) {
        setAppRoles(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getAvailableMenus = async () => {
    try {
      const response = await getAvailableStaffMenus();

      if (response?.data?.status) {
        const data = response?.data?.data || {};

        const groups = data.groups || [];
        const requiredRoutes = data.required_routes || [];

        setAvailableMenuGroups(Array.isArray(groups) ? groups : []);
        setRequiredMenuRoutes(
          Array.isArray(requiredRoutes) ? requiredRoutes : [],
        );

        // Mandatory routes are selected automatically.
        setSelectedMenuRoutes(
          Array.isArray(requiredRoutes) ? requiredRoutes : [],
        );
      } else {
        SweetAlertShow(
          "info",
          "Warning",
          response?.data?.message || "Unable to load staff menus.",
        );
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    }
  };

  const getUserRoles = async () => {
    setLoading(true);
    try {
      const response = await RoleView();
      if (response?.data.status) {
        setRoles(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getUserRoles();
    getAppRoles();
    getAvailableMenus();
  }, []);

  const getSubjects = async () => {
    setLoading(true);
    try {
      const response = await subjectList();
      if (response.data.status) {
        setSubjects(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getStaffId = async () => {
    const addUrl = `&registration_no=${staff_id}`;
    setLoading(true);
    try {
      const response = await VerifyStaffId(addUrl);

      if (response.data.status) {
        if (staff_id === false) {
          setStaffId(response?.data?.data?.next_registration_no ?? "");
        }
        setRegDetail(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const getState = async () => {
    setLoading(true);
    try {
      const response = await StateFetch();
      if (response.data) {
        setStates(response.data.states);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDistrict = async (state = "") => {
    setLoading(true);
    try {
      const response = await DistrictFetch(state);
      if (response.data) {
        setDistricts(response.data.districts);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getSubjects();
    getState();
    getDistrict();
  }, []);

  useEffect(() => {
    getStaffId();
  }, [staff_id]);

  const createStaff = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData(e.target);
      formData.append("subjects", JSON.stringify(selectedSubjects));
      selectedAppRoles.forEach((id) => formData.append("app_role_ids[]", id));
      const selectedOptionalMenus = selectedMenuRoutes.filter(
        (route) => !requiredMenuRoutes.includes(route),
      );

      // Save mobile menu routes only if at least one App Role
      // or at least one optional mobile menu was selected.
      const shouldSaveMenus =
        selectedAppRoles.length > 0 || selectedOptionalMenus.length > 0;

      if (shouldSaveMenus) {
        selectedMenuRoutes.forEach((route) => {
          formData.append("menu_routes[]", route);
        });
      }

      const response = await addStaff(formData);
      if (response?.data?.status) {
        setError("");

        // NOTE: adjust these keys to match whatever your addStaff API
        // actually returns for the newly created staff record.
        const newId = response?.data?.data?.id;
        const newName = response?.data?.data?.name;
        const newRegNo = response?.data?.data?.registration_no;

        setCreatedStaffId(newId);
        setCreatedStaffName(newName);
        setCreatedRegNo(newRegNo);

        SweetAlertShow("success", "Successfully", response?.data?.message);

        if (newId && wantsEmploymentStep) {
          // Unlock & move to Step 2
          setActiveStep(2);
        } else {
          // No employment step needed (or no id returned) -> go straight to profile
          navigate(newId ? `/teachers/view/${newId}` : "/staffs");
        }
      } else {
        if (response?.data?.errors) {
          setError(response?.data?.errors);
        }
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      if (error?.response?.data?.errors) {
        setError(error?.response?.data?.errors);
      }
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ??
          error?.response?.message ??
          error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  STEP 2 - EMPLOYMENT DETAILS (ported from SaleryDetails "Add      */
  /*  Salary" modal, now inline instead of in a Bootstrap modal)       */
  /* ---------------------------------------------------------------- */
  const [workingSchedules, setWorkingSchedules] = useState([]);
  const [empData, setEmpData] = useState({
    joining_date: new Date().toISOString().split("T")[0],
    salary_type: "",
    working_schedule_id: "",
    total_hours_per_day: "",
    current_salary: "",
    current_overtime_rate: "",
    paid_leaves_per_month: "",
    carry_forward: "2",
    max_carry_forward: "",
    remarks: "",
  });
  const [workingDays, setWorkingDays] = useState([]);

  const daysList = [
    { key: "Sun", label: "Sun" },
    { key: "Mon", label: "Mon" },
    { key: "Tue", label: "Tue" },
    { key: "Wed", label: "Wed" },
    { key: "Thu", label: "Thu" },
    { key: "Fri", label: "Fri" },
    { key: "Sat", label: "Sat" },
  ];

  const getWorkingSchedule = async () => {
    setLoading(true);
    try {
      const response = await workingSchedulesList("");
      if (response?.data?.status) {
        setWorkingSchedules(response?.data?.data);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWorkingSchedule();
  }, []);

  const selectedSchedule = workingSchedules?.find(
    (s) => s.id == empData.working_schedule_id,
  );

  useEffect(() => {
    if (empData.salary_type === "Monthly" && selectedSchedule) {
      setEmpData((prev) => ({
        ...prev,
        total_hours_per_day: selectedSchedule.total_hours_per_day,
      }));
    }
  }, [empData.salary_type, empData.working_schedule_id]);

  useEffect(() => {
    if (!empData?.working_schedule_id) {
      setWorkingDays([]);
      return;
    }

    const schedule = workingSchedules.find(
      (s) => String(s.id) === String(empData.working_schedule_id),
    );

    if (schedule?.working_days) {
      try {
        const parsedDays =
          typeof schedule.working_days === "string"
            ? JSON.parse(schedule.working_days)
            : schedule.working_days;
        setWorkingDays(Array.isArray(parsedDays) ? parsedDays : []);
      } catch (err) {
        console.error("Invalid working_days format", err);
        setWorkingDays([]);
      }
    } else {
      setWorkingDays([]);
    }
  }, [empData?.working_schedule_id, workingSchedules]);

  const handleDayChange = (dayKey) => {
    setWorkingDays((prev) =>
      prev.includes(dayKey)
        ? prev.filter((d) => d !== dayKey)
        : [...prev, dayKey],
    );
  };

  const handleEmpChange = (field, value) => {
    setEmpData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAddEmployment = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("staff_id", createdStaffId);
      formData.append("joining_date", empData.joining_date);
      formData.append("salary_type", empData.salary_type);
      formData.append("working_schedule_id", empData.working_schedule_id || "");
      formData.append("total_hours_per_day", empData.total_hours_per_day);
      formData.append("current_salary", empData.current_salary);
      formData.append("current_overtime_rate", empData.current_overtime_rate);
      formData.append("paid_leaves_per_month", empData.paid_leaves_per_month);
      formData.append("carry_forward", empData.carry_forward);
      formData.append("max_carry_forward", empData.max_carry_forward || "");
      formData.append("remarks", empData.remarks || "");
      formData.append("working_days", JSON.stringify(workingDays));

      const fileInput = document.getElementById("emp_joining_img");
      if (fileInput?.files?.[0]) {
        formData.append("joining_img", fileInput.files[0]);
      }

      const response = await EmploymentAdd(formData);
      if (response?.data?.status) {
        SweetAlertShow(
          "success",
          "Successfully",
          response?.data?.message ?? "Employment details added",
        );
        navigate(`/teachers/view/${createdStaffId}`);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (err) {
      SweetAlertShow(
        "error",
        "Error",
        err?.response?.data?.message ?? err?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  const skipEmployment = () => {
    navigate(`/teachers/view/${createdStaffId}`);
  };

  /* ---------------------------------------------------------------- */
  /*  RENDER                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />
          <div className="layout-page">
            <Nav />
            <div className="container-fluid mt-4">
              <div className="row">
                <StaffLayout />
                <div className="col-12">
                  <div className="card border-0 shadow-sm">
                    <div className="card-body p-4">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-circle d-flex align-items-center justify-content-center"
                            style={{
                              width: 65,
                              height: 65,
                              background:
                                "linear-gradient(135deg,#696cff,#8b5cf6)",
                              color: "#fff",
                              fontSize: "28px",
                            }}
                          >
                            <i className="ri-user-add-line"></i>
                          </div>

                          <div className="ms-3">
                            <h3 className="fw-bold mb-1">Add New Staff</h3>

                            <p className="text-muted mb-0">
                              Create a new staff profile in two simple steps.
                            </p>
                          </div>
                        </div>

                        <Link to="/staffs" className="btn btn-outline-primary">
                          <i className="ri-arrow-left-line me-1"></i>
                          Back to Staff List
                        </Link>
                      </div>

                      <hr className="my-4" />

                      {/* ---------------- STEPPER HEADER ---------------- */}
                      {wantsEmploymentStep && (
                        <div className="d-flex align-items-center justify-content-center mb-4">
                          <div className="d-flex align-items-center">
                            <span
                              className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                              style={{
                                width: 38,
                                height: 38,
                                background: createdStaffId
                                  ? "#22c55e"
                                  : activeStep === 1
                                    ? "#7c3aed"
                                    : "#e9ecef",
                                color:
                                  createdStaffId || activeStep === 1
                                    ? "#fff"
                                    : "#6c757d",
                              }}
                            >
                              {createdStaffId ? (
                                <i className="ri-check-line"></i>
                              ) : (
                                1
                              )}
                            </span>
                            <span
                              className={`ms-2 fw-semibold ${
                                activeStep === 1 ? "text-primary" : ""
                              }`}
                            >
                              Staff Details
                            </span>
                          </div>

                          <div
                            className="mx-3"
                            style={{
                              width: 70,
                              height: 2,
                              background: createdStaffId
                                ? "#22c55e"
                                : "#dee2e6",
                            }}
                          ></div>

                          <div className="d-flex align-items-center">
                            <span
                              className="rounded-circle d-flex align-items-center justify-content-center fw-bold"
                              style={{
                                width: 38,
                                height: 38,
                                background:
                                  activeStep === 2 ? "#7c3aed" : "#e9ecef",
                                color: activeStep === 2 ? "#fff" : "#6c757d",
                              }}
                            >
                              2
                            </span>
                            <span
                              className={`ms-2 fw-semibold ${
                                activeStep === 2 ? "text-primary" : "text-muted"
                              }`}
                            >
                              Employment Details
                            </span>
                          </div>
                        </div>
                      )}

                      {/* ================= STEP 1 : STAFF DETAILS ================= */}
                      {activeStep === 1 && (
                        <form onSubmit={createStaff} className="row g-4">
                          {/* ---------------- BASIC DETAILS ---------------- */}
                          <div className="col-12">
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <span
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: 32,
                                  height: 32,
                                  background: "rgba(124, 58, 237, 0.1)",
                                  color: "#7c3aed",
                                  fontSize: 16,
                                }}
                              >
                                <i className="ri-user-3-line"></i>
                              </span>
                              <h5 className="m-0 fw-bold">Basic Details</h5>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                name="registration_no"
                                className="form-control"
                                placeholder="Registration No"
                                required
                                value={staff_id}
                                onChange={(e) => {
                                  setStaffId(e.target.value);
                                }}
                              />
                              <label>
                                Registration No{" "}
                                <span className="text-danger">*</span>
                              </label>
                              {regDetail?.staff && (
                                <div className="text-danger fs-10 mt-1">
                                  <i className="ri-error-warning-line me-1"></i>
                                  Registration already exists for{" "}
                                  {regDetail.staff.name} (Reg No:{" "}
                                  {regDetail.staff.registration_no})
                                </div>
                              )}
                              <span className="text-danger">
                                {error?.registration_no ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                className="form-control input-uppercase"
                                name="name"
                                id="name"
                                placeholder="Name"
                                required
                                onChange={(e) =>
                                  (e.target.value = e.target.value.replace(
                                    /\b\w/g,
                                    (c) => c.toUpperCase(),
                                  ))
                                }
                                defaultValue={regDetail?.staff?.name}
                              />
                              <label htmlFor="name">
                                Name <span className="text-danger">*</span>
                              </label>
                              <span className="text-danger">
                                {error?.name ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="tel"
                                className="form-control"
                                placeholder="Mobile No"
                                name="mobile"
                                maxLength={10}
                                onInput={(e) => {
                                  e.target.value = e.target.value.replace(
                                    /[^0-9]/g,
                                    "",
                                  );
                                }}
                                required
                              />
                              <label htmlFor="mobile">
                                Mobile No <span className="text-danger">*</span>
                              </label>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="email"
                                className="form-control"
                                placeholder="Email"
                                name="email"
                              />
                              <label htmlFor="Email">Email</label>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <textarea
                                className="form-control input-uppercase"
                                name="father_name"
                                id="father_name"
                                placeholder="Father Name"
                                style={{ minHeight: "58px" }}
                                onChange={(e) =>
                                  (e.target.value = e.target.value.replace(
                                    /\b\w/g,
                                    (c) => c.toUpperCase(),
                                  ))
                                }
                                required
                              ></textarea>
                              <label htmlFor="father_name">
                                Father Name{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <span className="text-danger">
                                {error?.father_name ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="gender"
                                id="gender"
                                className="form-select"
                              >
                                <option value="Male">Male</option>
                                <option value="Female">Female</option>
                                <option value="Other">Other</option>
                              </select>
                              <label htmlFor="gender">
                                Gender <span className="text-danger">*</span>
                              </label>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <select name="category" className="form-select">
                                <option value="" selected disabled>
                                  Select
                                </option>
                                <option value="General">General</option>
                                <option value="OBC">OBC</option>
                                <option value="SC">SC</option>
                                <option value="ST">ST</option>
                                <option value="EWS">EWS</option>
                              </select>
                              <label>Category</label>
                              <span className="text-danger">
                                {error?.category ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="date"
                                name="dob"
                                className="form-control"
                              />
                              <label>Date of Birth</label>
                              <span className="text-danger">
                                {error?.dob ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-3">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                name="referred_by"
                                className="form-control"
                                placeholder="Referred By"
                              />
                              <label>Referred By</label>
                              <span className="text-danger">
                                {error?.referred_by ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="d-flex align-items-center gap-3 border rounded-3 p-3 h-100">
                              <span
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                  width: 44,
                                  height: 44,
                                  background: "rgba(124, 58, 237, 0.08)",
                                  color: "#7c3aed",
                                }}
                              >
                                <i className="ri-image-add-line fs-4"></i>
                              </span>
                              <div className="flex-grow-1">
                                <label className="form-label small fw-semibold mb-1 d-block">
                                  Profile Image
                                </label>
                                <input
                                  type="file"
                                  className="form-control form-control-sm"
                                  id="img"
                                  name="img"
                                  accept="image/*"
                                  capture="environment"
                                />
                              </div>
                            </div>
                          </div>

                          {/* ---------------- ADDRESS ---------------- */}
                          <div className="col-12">
                            <hr />
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <span
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: 32,
                                  height: 32,
                                  background: "rgba(124, 58, 237, 0.1)",
                                  color: "#7c3aed",
                                  fontSize: 16,
                                }}
                              >
                                <i className="ri-map-pin-line"></i>
                              </span>
                              <h5 className="m-0 fw-bold">Address</h5>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                name="village"
                                className="form-control input-uppercase"
                                placeholder="Village"
                              />
                              <label>Village</label>
                              <span className="text-danger">
                                {error?.village ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                className="form-select"
                                name="state"
                                onChange={(e) => {
                                  getDistrict(e.target.value);
                                }}
                              >
                                <option selected disabled>
                                  Select State
                                </option>
                                {states?.map((state) => (
                                  <option key={state.id} value={state.name}>
                                    {state.name}
                                  </option>
                                ))}
                              </select>
                              <label>State</label>
                              <span className="text-danger">
                                {error?.state ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <select className="form-select" name="district">
                                <option selected disabled>
                                  Select District
                                </option>
                                {districts?.map((district) => (
                                  <option
                                    key={district.id}
                                    value={district.name}
                                  >
                                    {district.name}
                                  </option>
                                ))}
                              </select>
                              <label>District</label>
                              <span className="text-danger">
                                {error?.district ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                name="pincode"
                                className="form-control"
                                placeholder="Pin Code"
                                minLength={6}
                                maxLength={6}
                              />
                              <label>Pin Code</label>
                              <span className="text-danger">
                                {error?.pincode ?? ""}
                              </span>
                            </div>
                          </div>

                          {/* ---------------- EXPERIENCE ---------------- */}
                          <div className="col-12">
                            <hr />
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: 32,
                                  height: 32,
                                  background: "rgba(124, 58, 237, 0.1)",
                                  color: "#7c3aed",
                                  fontSize: 16,
                                }}
                              >
                                <i className="ri-briefcase-4-line"></i>
                              </span>
                              <h5 className="m-0 fw-bold">Experience</h5>
                            </div>
                            <p className="text-muted small mb-3 ms-1">
                              Previous work / education background (optional)
                            </p>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <textarea
                                name="last_education"
                                className="form-control"
                                placeholder="Education"
                                style={{ minHeight: "58px" }}
                              ></textarea>
                              <label>Last Education</label>
                              <span className="text-danger">
                                {error?.education ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                name="last_school"
                                className="form-control input-uppercase"
                                placeholder="School"
                                onChange={(e) =>
                                  (e.target.value = e.target.value.replace(
                                    /\b\w/g,
                                    (c) => c.toUpperCase(),
                                  ))
                                }
                              />
                              <label>Last School / Institute</label>
                              <span className="text-danger">
                                {error?.school ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                name="last_designation"
                                className="form-control input-uppercase"
                                placeholder="Last Designation"
                                onChange={(e) =>
                                  (e.target.value = e.target.value.replace(
                                    /\b\w/g,
                                    (c) => c.toUpperCase(),
                                  ))
                                }
                              />
                              <label>Last Designation</label>
                              <span className="text-danger">
                                {error?.last_designation ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="form-floating form-floating-outline">
                              <input
                                type="text"
                                name="last_company"
                                className="form-control input-uppercase"
                                placeholder="Last Company"
                                onChange={(e) =>
                                  (e.target.value = e.target.value.replace(
                                    /\b\w/g,
                                    (c) => c.toUpperCase(),
                                  ))
                                }
                              />
                              <label>Last Company</label>
                              <span className="text-danger">
                                {error?.last_company ?? ""}
                              </span>
                            </div>
                          </div>

                          <div className="col-md-4">
                            <div className="d-flex align-items-center gap-3 border rounded-3 p-3 h-100">
                              <span
                                className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                                style={{
                                  width: 44,
                                  height: 44,
                                  background: "rgba(124, 58, 237, 0.08)",
                                  color: "#7c3aed",
                                }}
                              >
                                <i className="ri-file-text-line fs-4"></i>
                              </span>
                              <div className="flex-grow-1">
                                <label
                                  htmlFor="resume"
                                  className="form-label small fw-semibold mb-1 d-block"
                                >
                                  Resume (PDF/DOC)
                                </label>
                                <input
                                  type="file"
                                  className="form-control form-control-sm"
                                  id="resume"
                                  name="resume"
                                  accept=".pdf,.doc,.docx"
                                />
                                <span className="text-danger small">
                                  {error?.resume ?? ""}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* ---------------- ROLE / ACCESS ---------------- */}
                          <div className="col-12">
                            <hr />
                            <div className="d-flex align-items-center gap-2 mb-3">
                              <span
                                className="rounded-circle d-flex align-items-center justify-content-center"
                                style={{
                                  width: 32,
                                  height: 32,
                                  background: "rgba(124, 58, 237, 0.1)",
                                  color: "#7c3aed",
                                  fontSize: 16,
                                }}
                              >
                                <i className="ri-shield-user-line"></i>
                              </span>
                              <h5 className="m-0 fw-bold">
                                Role / Access Details
                              </h5>
                            </div>
                          </div>

                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="staff_type"
                                className="form-select"
                                required
                                onChange={(e) => setStaffType(e.target.value)}
                              >
                                <option value="">Select Type</option>
                                <option value="Teaching">Teaching staff</option>
                                <option value="Administrative">
                                  Administrative staff
                                </option>
                                <option value="Other">Other staff</option>
                              </select>
                              <label>
                                Staff Type{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <span className="text-danger">
                                {error?.type_id ?? ""}
                              </span>
                            </div>
                          </div>

                          {/* <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="role_id"
                                id="role_id"
                                className="form-select"
                              >
                                <option value="" selected disabled>
                                  Select Role
                                </option>
                                {roles?.map((role, i) => (
                                  <option key={i} value={role?.id}>
                                    {role?.name}
                                  </option>
                                ))}
                              </select>
                              <label htmlFor="role_id">Staff Access/Role</label>
                            </div>
                          </div> */}
                          {/* 
                          <div className="col-md-6">
                            <div className="form-floating form-floating-outline">
                              <select
                                name="role_id"
                                id="role_id"
                                className="form-select"
                                defaultValue=""
                              >
                                <option value="">Not Super Admin</option>
                                <option value="1">Super Admin</option>
                              </select>

                              <label htmlFor="role_id">Staff Access/Role</label>
                            </div>
                          </div> */}

                          <div className="col-12">
                            <div className="border rounded-3 p-3">
                              <label className="fw-semibold mb-2 d-flex align-items-center gap-1">
                                <i className="ri-shield-user-line text-primary"></i>
                                App Roles
                                <span className="text-muted small fw-normal ms-1">
                                  (select one or more)
                                </span>
                              </label>

                              <div className="d-flex flex-wrap gap-2">
                                {appRoles?.map((role) => {
                                  const checked = selectedAppRoles.includes(
                                    role.id,
                                  );
                                  return (
                                    <label
                                      key={role.id}
                                      htmlFor={`app-role-${role.id}`}
                                      className="d-flex align-items-center gap-2 border rounded-2 px-3 py-2 m-0"
                                      style={{
                                        minWidth: 160,
                                        cursor: "pointer",
                                        background: checked
                                          ? "rgba(124, 58, 237, 0.06)"
                                          : "transparent",
                                        borderColor: checked
                                          ? "#7c3aed"
                                          : undefined,
                                      }}
                                    >
                                      <input
                                        type="checkbox"
                                        className="form-check-input m-0 flex-shrink-0"
                                        id={`app-role-${role.id}`}
                                        checked={checked}
                                        onChange={() =>
                                          handleAppRoleChange(role.id)
                                        }
                                      />
                                      <span className="flex-grow-1">
                                        {role.name}
                                      </span>
                                    </label>
                                  );
                                })}
                              </div>

                              <span className="text-danger">
                                {error?.app_role_ids ?? ""}
                              </span>
                            </div>
                          </div>

                          {/* ---------------- MOBILE MENU ACCESS ---------------- */}
                          <div className="col-12">
                            <div className="border rounded-3 p-3">
                              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                <label className="fw-semibold d-flex align-items-center gap-2 mb-0">
                                  <i className="ri-smartphone-line text-primary fs-5"></i>
                                  Mobile App Menu Access
                                </label>

                                <span className="badge bg-label-primary">
                                  {selectedMenuRoutes.length} selected
                                </span>
                              </div>

                              <p className="text-muted small mb-3">
                                Select the screens this staff member can access
                                in the mobile app. Leave optional menus
                                unselected if they are not required.
                              </p>

                              {availableMenuGroups.length === 0 ? (
                                <div className="text-muted small">
                                  No menu groups available.
                                </div>
                              ) : (
                                availableMenuGroups.map((group, groupIndex) => (
                                  <div
                                    className="mb-4"
                                    key={group.id ?? group.name ?? groupIndex}
                                  >
                                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                      <div className="fw-semibold">
                                        {group.title ??
                                          group.name ??
                                          group.label ??
                                          "Menus"}
                                      </div>

                                      {(() => {
                                        const groupRoutes = (group.items || [])
                                          .map((item) => item.route)
                                          .filter(Boolean);

                                        const optionalRoutes =
                                          groupRoutes.filter(
                                            (route) =>
                                              !requiredMenuRoutes.includes(
                                                route,
                                              ),
                                          );

                                        const allSelected =
                                          groupRoutes.length > 0 &&
                                          groupRoutes.every((route) =>
                                            selectedMenuRoutes.includes(route),
                                          );

                                        return (
                                          <div className="form-check mb-0">
                                            <input
                                              type="checkbox"
                                              className="form-check-input"
                                              id={`select-all-menu-group-${group.id ?? groupIndex}`}
                                              checked={allSelected}
                                              disabled={
                                                optionalRoutes.length === 0
                                              }
                                              onChange={() =>
                                                handleSelectAllGroupMenus(group)
                                              }
                                            />

                                            <label
                                              className="form-check-label small fw-semibold"
                                              htmlFor={`select-all-menu-group-${group.id ?? groupIndex}`}
                                              style={{ cursor: "pointer" }}
                                            >
                                              Select All
                                            </label>
                                          </div>
                                        );
                                      })()}
                                    </div>

                                    <div className="row g-2">
                                      {(group.items || []).map((item) => {
                                        const route = item.route;
                                        if (!route) return null;

                                        const required =
                                          requiredMenuRoutes.includes(route);
                                        const checked =
                                          selectedMenuRoutes.includes(route);

                                        return (
                                          <div
                                            className="col-md-4 col-sm-6"
                                            key={route}
                                          >
                                            <label
                                              htmlFor={`staff-menu-${route}`}
                                              className="d-flex align-items-center gap-2 border rounded-2 p-3 h-100"
                                              style={{
                                                cursor: required
                                                  ? "not-allowed"
                                                  : "pointer",
                                                background: checked
                                                  ? "rgba(124, 58, 237, 0.06)"
                                                  : "transparent",
                                                borderColor: checked
                                                  ? "#7c3aed"
                                                  : undefined,
                                              }}
                                            >
                                              <input
                                                type="checkbox"
                                                className="form-check-input m-0 flex-shrink-0"
                                                id={`staff-menu-${route}`}
                                                checked={checked}
                                                disabled={required}
                                                onChange={() =>
                                                  handleMenuChange(route)
                                                }
                                              />

                                              <span className="flex-grow-1">
                                                {item.title ??
                                                  item.name ??
                                                  route}

                                                {required && (
                                                  <span className="d-block text-muted small">
                                                    Mandatory
                                                  </span>
                                                )}
                                              </span>
                                            </label>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>

                          {staffType == "Teaching" && (
                            <div className="col-12">
                              <div className="border rounded-3 p-3">
                                <label className="fw-semibold mb-2 d-flex align-items-center gap-1">
                                  <i className="ri-book-open-line text-primary"></i>
                                  Subjects{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <div className="d-flex flex-wrap gap-2">
                                  {subjects?.map((subject, i) => {
                                    const checked = selectedSubjects.includes(
                                      subject.id,
                                    );
                                    return (
                                      <label
                                        key={i}
                                        htmlFor={`subject-${subject.id}`}
                                        className="d-flex align-items-center gap-2 border rounded-2 px-3 py-2 m-0"
                                        style={{
                                          minWidth: 160,
                                          cursor: "pointer",
                                          background: checked
                                            ? "rgba(124, 58, 237, 0.06)"
                                            : "transparent",
                                          borderColor: checked
                                            ? "#7c3aed"
                                            : undefined,
                                        }}
                                      >
                                        <input
                                          type="checkbox"
                                          className="form-check-input m-0 flex-shrink-0"
                                          id={`subject-${subject.id}`}
                                          value={subject.id}
                                          checked={checked}
                                          onChange={(e) =>
                                            handleSubjectChange(e.target.value)
                                          }
                                        />
                                        <span className="flex-grow-1">
                                          {subject.name}
                                        </span>
                                      </label>
                                    );
                                  })}
                                </div>
                                <span className="text-danger">
                                  {error?.subjects ?? ""}
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="col-12 mt-3 text-end">
                            <button
                              type="submit"
                              className="btn btn-primary px-5 py-2"
                            >
                              {wantsEmploymentStep
                                ? "Save & Continue"
                                : "Submit"}
                              <i className="ri-arrow-right-line ms-1"></i>
                            </button>
                          </div>
                        </form>
                      )}

                      {/* ============= STEP 2 : EMPLOYMENT DETAILS ============= */}
                      {activeStep === 2 && (
                        <>
                          <div className="alert alert-success d-flex align-items-center mb-4">
                            <i className="ri-checkbox-circle-line fs-4 me-2"></i>
                            <div>
                              <strong>{createdStaffName || "Staff"}</strong>
                              {createdRegNo
                                ? ` (Reg No: ${createdRegNo})`
                                : ""}{" "}
                              was added successfully. You can add employment /
                              salary details now, or skip and do it later.
                            </div>
                          </div>

                          <form
                            onSubmit={handleAddEmployment}
                            className="row g-4"
                          >
                            <div className="col-md-12">
                              <h5 className="m-0">
                                Employment / Salary Details
                              </h5>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="date"
                                  className="form-control"
                                  name="joining_date"
                                  required
                                  value={empData.joining_date}
                                  onChange={(e) =>
                                    handleEmpChange(
                                      "joining_date",
                                      e.target.value,
                                    )
                                  }
                                />
                                <label>
                                  Joining Date{" "}
                                  <span className="text-danger">*</span>
                                </label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-control"
                                  name="salary_type"
                                  required
                                  value={empData.salary_type}
                                  onChange={(e) =>
                                    handleEmpChange(
                                      "salary_type",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="">Select Salary Type</option>
                                  <option value="Monthly">Monthly</option>
                                  <option value="Hourly">Hourly</option>
                                </select>
                                <label>Salary Type</label>
                              </div>
                            </div>

                            {empData.salary_type === "Monthly" && (
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <select
                                    className="form-control"
                                    name="working_schedule_id"
                                    value={empData.working_schedule_id}
                                    onChange={(e) =>
                                      handleEmpChange(
                                        "working_schedule_id",
                                        e.target.value,
                                      )
                                    }
                                  >
                                    <option value="">
                                      -- No Working Schedule --
                                    </option>
                                    {workingSchedules.map((schedule) => (
                                      <option
                                        key={schedule.id}
                                        value={schedule.id}
                                      >
                                        {schedule.name} (
                                        {formatTime(schedule.start_time)} -{" "}
                                        {formatTime(schedule.end_time)})
                                      </option>
                                    ))}
                                  </select>
                                  <label>Working Schedule</label>
                                </div>
                              </div>
                            )}

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="number"
                                  className="form-control"
                                  name="total_hours_per_day"
                                  required
                                  value={empData.total_hours_per_day}
                                  readOnly={
                                    empData.salary_type === "Monthly" &&
                                    !!selectedSchedule
                                  }
                                  onChange={(e) =>
                                    handleEmpChange(
                                      "total_hours_per_day",
                                      e.target.value,
                                    )
                                  }
                                />
                                <label>Working Hours Per Day</label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="number"
                                  className="form-control"
                                  name="current_salary"
                                  required
                                  value={empData.current_salary}
                                  onChange={(e) =>
                                    handleEmpChange(
                                      "current_salary",
                                      e.target.value,
                                    )
                                  }
                                />
                                <label>Salary</label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="number"
                                  className="form-control"
                                  name="current_overtime_rate"
                                  value={empData.current_overtime_rate}
                                  onChange={(e) =>
                                    handleEmpChange(
                                      "current_overtime_rate",
                                      e.target.value,
                                    )
                                  }
                                />
                                <label>Overtime Rate (Per Hours)</label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="number"
                                  className="form-control"
                                  name="paid_leaves_per_month"
                                  value={empData.paid_leaves_per_month}
                                  onChange={(e) =>
                                    handleEmpChange(
                                      "paid_leaves_per_month",
                                      e.target.value,
                                    )
                                  }
                                />
                                <label>Paid Leaves Per Month</label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <select
                                  className="form-select"
                                  name="carry_forward"
                                  value={empData.carry_forward}
                                  onChange={(e) =>
                                    handleEmpChange(
                                      "carry_forward",
                                      e.target.value,
                                    )
                                  }
                                >
                                  <option value="2">No</option>
                                  <option value="1">Yes</option>
                                </select>
                                <label>Paid Leaves Carry Forward</label>
                              </div>
                            </div>

                            {empData.carry_forward === "1" && (
                              <div className="col-md-6">
                                <div className="form-floating form-floating-outline">
                                  <input
                                    type="number"
                                    className="form-control"
                                    name="max_carry_forward"
                                    value={empData.max_carry_forward}
                                    onChange={(e) =>
                                      handleEmpChange(
                                        "max_carry_forward",
                                        e.target.value,
                                      )
                                    }
                                  />
                                  <label>
                                    Maximum Paid Leaves Carry Forward
                                  </label>
                                </div>
                              </div>
                            )}

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <textarea
                                  className="form-control input-uppercase"
                                  name="remarks"
                                  value={empData.remarks}
                                  onChange={(e) =>
                                    handleEmpChange("remarks", e.target.value)
                                  }
                                />
                                <label>Remark</label>
                              </div>
                            </div>

                            <div className="col-md-6">
                              <div className="form-floating form-floating-outline">
                                <input
                                  type="file"
                                  className="form-control"
                                  name="joining_img"
                                  id="emp_joining_img"
                                />
                                <label>Joining Letter Img</label>
                              </div>
                            </div>

                            <div className="col-md-12">
                              <label className="fw-semibold mb-2">
                                Working Days
                              </label>
                              <div className="d-flex flex-wrap gap-4">
                                {daysList.map((day) => (
                                  <div className="form-check" key={day.key}>
                                    <input
                                      id={`emp_day_${day.key}`}
                                      type="checkbox"
                                      className="form-check-input"
                                      checked={workingDays.includes(day.key)}
                                      onChange={() => handleDayChange(day.key)}
                                    />
                                    <label
                                      className="form-check-label"
                                      htmlFor={`emp_day_${day.key}`}
                                    >
                                      {day.label}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <div className="col-md-12 mt-2 d-flex justify-content-end gap-2">
                              <button
                                type="button"
                                className="btn btn-outline-secondary px-10"
                                onClick={skipEmployment}
                              >
                                Skip for now
                              </button>
                              <button
                                type="submit"
                                className="btn btn-primary px-10"
                              >
                                Save & Finish
                              </button>
                            </div>
                          </form>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
